import { Tooltip } from '@chakra-ui/tooltip';
import {
  IActionReference,
  IEditorialActionReference
} from '@publishing-engine/domain/models';
import { Children, cloneElement, ReactNode } from 'react';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useSubmissionReviewsData } from 'src/contexts/SubmissionReviewsDataProvider';
import useAuthAccessQuery from 'src/hooks/useAuthAccessQuery';

interface Props {
  reviewId: string;
  actionName: IActionReference['name'];
  children: JSX.Element | JSX.Element[];
  entityType?: string;
  tooltipLabel?: ReactNode;
}

const ProtectedReviewWorkflowAction = ({
  reviewId,
  actionName,
  children,
  entityType = 'journal_phase',
  tooltipLabel = 'You do not have permission to perform this action'
}: Props) => {
  const { reviews } = useSubmissionReviewsData();
  const { workflowResponse, submissionContextId } = useSubmissionData();
  const currentState = workflowResponse.workflowData.currentState;
  const entityId = `${submissionContextId}_${currentState}`;

  const review = reviews?.items.find(r => r.id === reviewId);

  if (!review) {
    return children;
  }

  const reviewWorkflowState = review.workflow.currentState;
  const actions =
    workflowResponse.articleWorkflow.transitionGraph.review.states[
      reviewWorkflowState
    ]?.actions || [];

  const action = actions.find(
    action => action.name === actionName
  ) as IEditorialActionReference;

  const { isAllowed, isLoading } = useAuthAccessQuery({
    relation: action?.accessLevel,
    entityType,
    entityId
  });

  return (
    <>
      {isAllowed
        ? children
        : Children.map(children, (child, index) => (
            <Tooltip
              key={index}
              isDisabled={isLoading}
              hasArrow
              label={tooltipLabel}
              fontSize="md"
              placement="top"
              shouldWrapChildren
            >
              {cloneElement(child, {
                isDisabled: true,
                isLoading,
                onClick: (e: Event) => e.preventDefault()
              })}
            </Tooltip>
          ))}
    </>
  );
};

export default ProtectedReviewWorkflowAction;
